import { Committee } from '../../../models/committees.types';
import { ExaminingTeamChairmen, ITaskGroup } from '../../../models/taskGroup.types';

const findExaminersBasedOnFunction = (examiners: ITaskGroup[], selectedFunctions: string[]) => {
  const result: string[] = [];
  if (selectedFunctions.length === 0) return [];
  const searchForZWO = selectedFunctions.includes('ZWO');
  const searchForKGZ = selectedFunctions.includes('KGZ');
  const searchForPZE = selectedFunctions.includes('PZE');
  const searchForExaminer = selectedFunctions.includes('Egzaminator');

  examiners.forEach((taskGroup: ITaskGroup) => {
    if (taskGroup.sampleAssessmentGroup) {
      const ZWO = taskGroup.sampleAssessmentGroup;
      if (ZWO) {
        if (ZWO.sampleAssessmentChairman.examiner) {
          if (ZWO.sampleAssessmentChairman.examiner?.shouldBeHighlighted && searchForZWO) {
            result.push(ZWO.sampleAssessmentChairman.examiner.examinerId);
          }
        }
        ZWO.sampleAssessmentMembers.forEach((member) => {
          if (member) {
            if (member.examiner?.shouldBeHighlighted) {
              result.push(member.examiner.examinerId);
            }
          }
        });
      }
    }
    const KGZ = taskGroup.examTaskGroupCoordinators;
    if (KGZ) {
      KGZ.forEach((kgz) => {
        if (kgz.examiner?.shouldBeHighlighted && searchForKGZ) {
          result.push(kgz.examiner.examinerId);
        }
        kgz.examiningTeamChairmen.forEach((examiningTeamChairmen: ExaminingTeamChairmen) => {
          if (examiningTeamChairmen.examiner?.shouldBeHighlighted && searchForPZE) {
            result.push(examiningTeamChairmen.examiner.examinerId);
          }
          examiningTeamChairmen.examiningTeam.examiningTeamMembers.forEach((member) => {
            if (member) {
              if (member.examiner?.shouldBeHighlighted && searchForExaminer) {
                result.push(member.examiner.examinerId);
              }
            }
          });
        });
      });
    }
  });

  return result;
};

const findExaminersBasedOnExamComitte = (examiners: ITaskGroup[], selectedExamComittee: string[]) => {
  const selectedCommittee = selectedExamComittee.map((x) => parseInt(x) as Committee);
  const result: string[] = [];

  examiners.forEach((taskGroup) => {
    const group = taskGroup.sampleAssessmentGroup;

    const chairmanCommitteeId = group?.sampleAssessmentChairman?.examiner?.examinerPermission[0]?.examCommitteeId;
    const chairmanExaminerId = group?.sampleAssessmentChairman?.examiner?.examinerId;
    if (chairmanCommitteeId && chairmanExaminerId) {
      if (selectedCommittee.includes(chairmanCommitteeId)) {
        result.push(chairmanExaminerId);
      }
    }

    group?.sampleAssessmentMembers?.forEach((member) => {
      const committeeId = member.examiner?.examinerPermission[0]?.examCommitteeId;
      if (committeeId && selectedCommittee.includes(committeeId)) {
        result.push(member.examiner?.examinerId);
      }
    });

    taskGroup.examTaskGroupCoordinators?.forEach((member) => {
      const committeeId = member.examiner?.examinerPermission[0]?.examCommitteeId;
      if (committeeId && selectedCommittee.includes(committeeId)) {
        result.push(member.examiner?.examinerId);
      }

      member.examiningTeamChairmen.forEach((chairmen) => {
        const committeeId = chairmen.examiner?.examinerPermission[0]?.examCommitteeId;
        if (committeeId && selectedCommittee.includes(committeeId)) {
          result.push(chairmen.examiner?.examinerId);
        }
      });
    });
  });

  return result;
};

export const findExaminersBasedOnFilter = (
  examiners: ITaskGroup[] | null,
  selectedFunctions: string[],
  selectedExamCommittee: string[],
): string[] => {
  if (!examiners) return [];

  const selectedFunctionsAreEmpty = selectedFunctions.length === 0;
  const selectedCommitteeAreEmpty = selectedExamCommittee.length === 0;
  if (selectedFunctionsAreEmpty && selectedCommitteeAreEmpty) return [];

  const foundIdsFunction = findExaminersBasedOnFunction(examiners, selectedFunctions);
  const foundIdsCommittee = findExaminersBasedOnExamComitte(examiners, selectedExamCommittee);

  if (selectedFunctionsAreEmpty) return foundIdsCommittee;

  if (selectedCommitteeAreEmpty) return foundIdsFunction;

  return [...new Set([...foundIdsFunction, ...foundIdsCommittee])];
};

export const handleTreemapExpansion = (
  examiners: ITaskGroup[],
  selectedFunctions: string[],
  selectedTaskGroups: string[],
  isAnyFilter: boolean,
  isAnyFilterWithoutTaskGroupActive: boolean,
) => {
  const expanded: string[] = [];
  const forceExpand = true;
  const blockUnderPZE = selectedFunctions.includes('PZE');
  const blockUnderKGZ = selectedFunctions.includes('KGZ');

  examiners.forEach((taskGroup: ITaskGroup) => {
    const expandedForTaskGroup: { id: string; expansionReason: boolean }[] = [];
    const isTaskGroupToExpand = selectedTaskGroups.some((e) => e === taskGroup.name);
    if (isTaskGroupToExpand || forceExpand) {
      expandedForTaskGroup.push({ id: taskGroup.taskGroupId, expansionReason: isTaskGroupToExpand });
    }
    if (taskGroup.sampleAssessmentGroup) {
      const ZWO = taskGroup.sampleAssessmentGroup;
      if (ZWO.sampleAssessmentChairman?.examiner?.shouldBeHighlighted || isTaskGroupToExpand || forceExpand) {
        expandedForTaskGroup.push({
          id: ZWO.sampleAssessmentsGroupId,
          expansionReason: ZWO.sampleAssessmentChairman?.examiner?.shouldBeHighlighted,
        });
        expandedForTaskGroup.push({
          id: taskGroup.taskGroupId,
          expansionReason: ZWO.sampleAssessmentChairman?.examiner?.shouldBeHighlighted,
        });
      }
      ZWO.sampleAssessmentMembers.forEach((sampleAssessmentMember) => {
        const shouldExpand =
          ZWO.sampleAssessmentChairman?.examiner?.shouldBeHighlighted ||
          sampleAssessmentMember?.examiner?.shouldBeHighlighted;
        if (shouldExpand || isTaskGroupToExpand || forceExpand) {
          expandedForTaskGroup.push({
            id: ZWO.sampleAssessmentsGroupId,
            expansionReason: shouldExpand,
          });
          expandedForTaskGroup.push({
            id: taskGroup.taskGroupId,
            expansionReason: shouldExpand,
          });
        }
      });
    }
    const KGZ = taskGroup.examTaskGroupCoordinators;
    if (KGZ) {
      KGZ.forEach((kgz) => {
        if (kgz?.examiner?.shouldBeHighlighted) {
          expandedForTaskGroup.push({
            id: taskGroup.taskGroupId,
            expansionReason: kgz?.examiner?.shouldBeHighlighted,
          });
          expandedForTaskGroup.push({
            id: kgz.examiner.examinerId as string,
            expansionReason: kgz?.examiner?.shouldBeHighlighted,
          });
        }
        kgz?.examiningTeamChairmen?.forEach((examiningTeamChairmen: ExaminingTeamChairmen) => {
          if (
            kgz?.examiner?.shouldBeHighlighted ||
            examiningTeamChairmen?.examiner?.shouldBeHighlighted ||
            isTaskGroupToExpand ||
            forceExpand
          ) {
            if (!blockUnderKGZ)
              expandedForTaskGroup.push({
                id: kgz.taskGroupCoordinatorId,
                expansionReason:
                  kgz?.examiner?.shouldBeHighlighted || examiningTeamChairmen?.examiner?.shouldBeHighlighted,
              });
            expandedForTaskGroup.push({
              id: taskGroup.taskGroupId,
              expansionReason:
                kgz?.examiner?.shouldBeHighlighted || examiningTeamChairmen?.examiner?.shouldBeHighlighted,
            });
          }
          const PZE = kgz.examiningTeamChairmen;
          if (PZE) {
            PZE.forEach((examiningTeamChairmen: ExaminingTeamChairmen) => {
              if (examiningTeamChairmen.examiner?.shouldBeHighlighted || isTaskGroupToExpand || forceExpand) {
                expandedForTaskGroup.push({
                  id: kgz.taskGroupCoordinatorId,
                  expansionReason: examiningTeamChairmen.examiner?.shouldBeHighlighted,
                });
                if (!blockUnderPZE)
                  expandedForTaskGroup.push({
                    id: examiningTeamChairmen.teamChairmanId,
                    expansionReason: examiningTeamChairmen.examiner?.shouldBeHighlighted,
                  });
              }
            });
          }
          examiningTeamChairmen.examiningTeam?.examiningTeamMembers?.forEach((member) => {
            if (member?.examiner?.shouldBeHighlighted || isTaskGroupToExpand || forceExpand) {
              expandedForTaskGroup.push({
                id: kgz.taskGroupCoordinatorId,
                expansionReason: member?.examiner?.shouldBeHighlighted,
              });
              expandedForTaskGroup.push({
                id: examiningTeamChairmen.teamChairmanId,
                expansionReason: member?.examiner?.shouldBeHighlighted,
              });
              expandedForTaskGroup.push({
                id: taskGroup.taskGroupId,
                expansionReason: member?.examiner?.shouldBeHighlighted,
              });
            }
          });
        });
      });
    }

    console.log('Expanded...', { expandedForTaskGroup, isAnyFilter });
    if (!isAnyFilter) expandedForTaskGroup.map((x) => x.id).forEach((id) => expanded.push(id));
    if (isAnyFilter) {
      if (selectedTaskGroups.some((e) => e === taskGroup.name && isAnyFilterWithoutTaskGroupActive)) {
        expandedForTaskGroup.map((x) => x.id).forEach((id) => expanded.push(id));
      } else {
        const toExpand = expandedForTaskGroup.filter((x) => x.expansionReason);

        // NOT If only taskGroup is in expandedForTaskGroup and this taskGroup is in selectedTaskGroups and any other filter is active
        if (
          !(
            toExpand.length === 1 &&
            selectedTaskGroups.some((e) => e === taskGroup.name && isAnyFilterWithoutTaskGroupActive)
          )
        ) {
          toExpand
            .filter((x) => x.expansionReason)
            .map((x) => x.id)
            .forEach((id) => expanded.push(id));
        }
      }
    }
  });

  return expanded;
};
